import { FaArrowRight, FaCircle } from "react-icons/fa6";
import { GoArrowUpRight } from "react-icons/go";
import { Link } from "react-router-dom";

import { toRoutePath } from "../../lib/seo";
import classes from "./ImageTextSection.module.css";

function renderBoldText(text, boldText) {
  if (!text) return null;

  if (!boldText) {
    return text;
  }

  const escapedText = boldText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedText})`, "i");

  return text.split(regex).map((part, index) => {
    if (part.toLowerCase() === boldText.toLowerCase()) {
      return <strong key={index}>{part}</strong>;
    }

    return part;
  });
}

function renderHighlightedText(text, highlightWords) {
  if (!text) return null;

  const words = Array.isArray(highlightWords)
    ? highlightWords
    : [highlightWords];

  const validWords = words.filter(Boolean);

  if (!validWords.length) {
    return text;
  }

  const escapedWords = validWords.map((word) =>
    word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );

  const regex = new RegExp(`(${escapedWords.join("|")})`, "gi");

  return text.split(regex).map((part, index) => {
    const isHighlighted = validWords.some(
      (word) => part.toLowerCase() === word.toLowerCase(),
    );

    return isHighlighted ? (
      <span key={index} className={classes.highlight}>
        {part}
      </span>
    ) : (
      part
    );
  });
}

function ListItems({ items = [], boldTexts = [], icon = false }) {
  if (!items.length) {
    return null;
  }

  return (
    <ul className={classes.list}>
      {items.map((item, index) => (
        <li
          key={index}
          className={`${classes.listItem} ${
            icon ? classes.listItemIcon : classes.listItemBullet
          }`}
        >
          {icon ? (
            <GoArrowUpRight
              className={classes.listIcon}
              aria-hidden="true"
              size={16}
            />
          ) : (
            <FaCircle className={classes.listIcon} aria-hidden="true" />
          )}

          <span>{renderBoldText(item, boldTexts[index])}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ImageTextSection({
  image = "",
  imageAlt = "",
  title = "",
  highlightWords = [],
  description = "",

  // Existing single subsection props
  subHeading = "",
  subHeadingHighlightWords = [],
  description2 = "",
  listItems = [],
  listItemBoldText = [],

  // New multiple subsection support
  subSections = [],

  imagePosition = "left",
  icon = false,
  buttonLabel = "",
  buttonUrl = "",
}) {
  const normalizedImagePosition = imagePosition === "right" ? "right" : "left";

  return (
    <section className={classes.section}>
      <div className={classes.shell}>
        <div
          className={`${classes.layout} ${
            normalizedImagePosition === "right"
              ? classes.imageRight
              : classes.imageLeft
          }`}
        >
          {/* Image */}
          {image && (
            <div className={classes.imageWrap}>
              <img
                src={image}
                alt={imageAlt}
                className={classes.image}
                loading="lazy"
              />
            </div>
          )}

          {/* Content */}
          <div className={classes.content}>
            {/* Main Title */}
            {title && (
              <h2 className={classes.title}>
                {renderHighlightedText(title, highlightWords)}
              </h2>
            )}

            {/* Main Description */}
            {description && (
              <p className={classes.description}>
                {description}
              </p>
            )}

            {/* Existing Single Subsection */}
            {subHeading && (
              <div className={classes.subSection}>
                <h3 className={classes.subHeading}>
                  {renderHighlightedText(subHeading, subHeadingHighlightWords)}
                </h3>

                {description2 && (
                  <p className={classes.description2}>{description2}</p>
                )}

                <ListItems
                  items={listItems}
                  boldTexts={listItemBoldText}
                  icon={icon}
                />
              </div>
            )}

            {/* Multiple Subsections */}
            {subSections.length > 0 &&
              subSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={classes.subSection}>
                  {section.heading && (
                    <h3 className={classes.subHeading}>
                      {renderHighlightedText(
                        section.heading,
                        section.highlightWords,
                      )}
                    </h3>
                  )}

                  {section.description && (
                    <p className={classes.description2}>
                      {section.description}
                    </p>
                  )}

                  <ListItems
                    items={section.listItems || []}
                    boldTexts={section.listItemBoldText || []}
                    icon={icon}
                  />
                </div>
              ))}

            {/* Button */}
            {buttonLabel && buttonUrl && (
              <Link to={toRoutePath(buttonUrl)} className={`btn ${classes.button}`}>
                {buttonLabel}
                <FaArrowRight aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
